import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import {useGetErrorList} from '../../../hooks/productions/useProductions';
import {GetErrorListRequestPayload} from '../../../api/types/production.type';
import '../Demo.scss';

const ErrorListApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<number>(15);
  const [productionSecurityGroupId, setProductionSecurityGroupId] =
    useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [displayLength, setDisplayLength] = useState<number>(10);
  const [sortingColumn, setSortingColumn] = useState<string>('');
  const [direction, setDirection] = useState<string>('asc');
  const [qcTypeLabel, setQcTypeLabel] = useState<string>('');
  const [enabled, setEnabled] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const requestPayload: GetErrorListRequestPayload = {
    productionId,
    productionSecurityGroupId,
    pageNumber,
    displayLength,
    sortingColumn,
    direction,
    qcTypeLabel,
  };

  const {
    data: errorListData,
    isLoading,
    error,
    refetch,
  } = useGetErrorList(requestPayload, enabled);

  const handleTestApi = () => {
    setEnabled(true);
  };

  const handleReset = () => {
    setEnabled(false);
  };

  const handleCopyJson = async () => {
    if (errorListData) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(errorListData, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Error List API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Get list of production errors with pagination, sorting, and filtering
        </Typography>
      </Box>
      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" />
            <Typography variant="h6" className="typo-text subhead2_size">
              Get Error List
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/errors/list
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Get list of production errors with pagination, sorting, and
            filtering. Returns error details including document IDs, error
            messages, and export status.
          </Typography>
        </CardContent>
      </Card>
      {/* Request Parameters */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Parameters
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            <TextField
              label="Production ID"
              type="number"
              value={productionId}
              onChange={(e) => setProductionId(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Production Security Group ID"
              type="number"
              value={productionSecurityGroupId}
              onChange={(e) =>
                setProductionSecurityGroupId(Number(e.target.value))
              }
              size="small"
            />
            <TextField
              label="Page Number"
              type="number"
              value={pageNumber}
              onChange={(e) => setPageNumber(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Display Length"
              type="number"
              value={displayLength}
              onChange={(e) => setDisplayLength(Number(e.target.value))}
              size="small"
            />
            <TextField
              label="Sorting Column"
              value={sortingColumn}
              onChange={(e) => setSortingColumn(e.target.value)}
              size="small"
              placeholder="Leave empty for default"
            />
            <TextField
              label="Direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              size="small"
              placeholder="asc or desc"
            />
            <TextField
              label="QC Type Label"
              value={qcTypeLabel}
              onChange={(e) => setQcTypeLabel(e.target.value)}
              size="small"
              placeholder="Leave empty for all"
            />
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestApi}
              data-testid="error-list-api-test-test-api-button">
              🚀 Test API
            </Button>
            <Button
              variant="outlined"
              onClick={handleReset}
              data-testid="error-list-api-test-reset-button">
              🔄 Reset
            </Button>
            {enabled && (
              <Button
                variant="outlined"
                onClick={() => refetch()}
                data-testid="error-list-api-test-refetch-button">
                ♻️ Refetch
              </Button>
            )}
            {errorListData && (
              <Button
                variant="outlined"
                onClick={handleCopyJson}
                data-testid="error-list-api-test-button">
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      {/* Loading State */}
      {isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">⏳ Loading... Making API request...</Alert>
          </CardContent>
        </Card>
      )}
      {/* Error State */}
      {error && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {error.message || 'An error occurred while fetching data'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}
      {/* Response Data */}
      {errorListData && !isLoading && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Response Data
              </Typography>
              <Box sx={{display: 'flex', gap: 1}}>
                <Chip
                  label={`Total Records: ${errorListData.totalRecords}`}
                  color="primary"
                  size="small"
                />
                <Chip
                  label={`Display Records: ${errorListData.totalDisplayRecords}`}
                  color="secondary"
                  size="small"
                />
              </Box>
            </Box>

            {errorListData.errorList && errorListData.errorList.length > 0 ? (
              <TableContainer component={Paper} sx={{maxHeight: 600}}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Error ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Document ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Doc ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>File Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Doc Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Native Exported</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Error Message</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {errorListData.errorList.map((errorItem, index) => (
                      <TableRow key={errorItem.generateErrorId || index}>
                        <TableCell>{errorItem.generateErrorId}</TableCell>
                        <TableCell>{errorItem.documentId}</TableCell>
                        <TableCell>{errorItem.docID}</TableCell>
                        <TableCell>{errorItem.fileName || 'N/A'}</TableCell>
                        <TableCell>{errorItem.docType}</TableCell>
                        <TableCell>
                          <Chip
                            label={errorItem.nativeExported ? 'Yes' : 'No'}
                            color={
                              errorItem.nativeExported ? 'success' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={errorItem.error}
                        >
                          {errorItem.error}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No errors found</Alert>
            )}
          </CardContent>
        </Card>
      )}
      {/* JSON Response */}
      {errorListData && !isLoading && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              JSON Response
            </Typography>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '12px',
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <pre>{JSON.stringify(errorListData, null, 2)}</pre>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ErrorListApiTest;
